"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth"
import { doc, setDoc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore"
import { auth, db } from "../firebase"
import {
  FaEye,
  FaEyeSlash,
  FaEnvelope,
  FaLock,
  FaUserShield,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaUser,
} from "react-icons/fa"

const Signin = () => {
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [fullName, setFullName] = useState("")
  const [error, setError] = useState("")
  const [popup, setPopup] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [pendingApproval, setPendingApproval] = useState(false)
  const [approvedAdmins, setApprovedAdmins] = useState([])
  const navigate = useNavigate()

  // Hardcoded admin emails
  const hardcodedAdmins = [
    "sivapratap2004@gmail.com",
    "iiche@cb.amrita.edu",
    "ananyasai.paruchuri@gmail.com",
    "cb.en.u4che22033@cb.students.amrita.edu",
    "sahithiuppari469@gmail.com",
  ]

  // Fetch approved admins from database on component mount
  useEffect(() => {
    fetchApprovedAdmins()
  }, [])

  const fetchApprovedAdmins = async () => {
    try {
      const approvedAdminsDoc = await getDoc(doc(db, "settings", "approvedAdmins"))
      if (approvedAdminsDoc.exists()) {
        const data = approvedAdminsDoc.data()
        setApprovedAdmins(data.emails || [])
      }
    } catch (error) {
      console.error("Error fetching approved admins:", error)
    }
  }

  // Check if user is admin (either hardcoded or approved)
  const isAdmin = (userEmail) => {
    return hardcodedAdmins.includes(userEmail) || approvedAdmins.includes(userEmail)
  }

  const checkAdminRequestStatus = async (userEmail) => {
    try {
      const requestDoc = await getDoc(doc(db, "adminRequests", userEmail))
      if (requestDoc.exists()) {
        const requestData = requestDoc.data()
        return requestData.status
      }
      return null
    } catch (error) {
      console.error("Error checking admin request status:", error)
      return null
    }
  }

  const createAdminRequest = async (userEmail) => {
    try {
      // Check if request already exists
      const existingRequestDoc = await getDoc(doc(db, "adminRequests", userEmail))

      if (existingRequestDoc.exists()) {
        const requestData = existingRequestDoc.data()
        if (requestData.status === "pending") {
          throw new Error("Admin access request already pending approval.")
        } else if (requestData.status === "approved") {
          // If approved, refresh the approved admins list
          await fetchApprovedAdmins()
          return "approved"
        }
      }

      // Create new admin request
      await setDoc(doc(db, "adminRequests", userEmail), {
        email: userEmail,
        status: "pending",
        requestedAt: serverTimestamp(),
        approvedBy: null,
        approvedAt: null,
      })

      // Create notification for all hardcoded admins
      for (const adminEmail of hardcodedAdmins) {
        await addDoc(collection(db, "adminNotifications"), {
          adminEmail: adminEmail,
          type: "admin_request",
          title: "New Admin Access Request",
          message: `${userEmail} has requested admin access to the system.`,
          requestEmail: userEmail,
          read: false,
          createdAt: serverTimestamp(),
        })
      }

      return "pending"
    } catch (error) {
      throw new Error("Failed to create admin request: " + error.message)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setPopup("")
    setPendingApproval(false)

    try {
      if (isSignUp) {
        // Handle Sign Up
        if (password !== confirmPassword) {
          setError("Passwords do not match.")
          setLoading(false)
          return
        }

        if (password.length < 6) {
          setError("Password must be at least 6 characters long.")
          setLoading(false)
          return
        }

        // Create user account
        await createUserWithEmailAndPassword(auth, email, password)

        // Check if user is admin
        await fetchApprovedAdmins()
        if (isAdmin(email)) {
          setPopup("Account created successfully! Redirecting to dashboard...")
          setTimeout(() => navigate("/dashboard"), 1500)
        } else {
          // Create admin request for non-admin users
          const status = await createAdminRequest(email)
          setPendingApproval(true)
          setPopup("Account created and access request submitted! Existing admins will be notified for approval.")
        }
      } else {
        // Handle Sign In
        // Refresh approved admins list before checking
        await fetchApprovedAdmins()

        if (isAdmin(email)) {
          // Admin login - proceed normally
          await signInWithEmailAndPassword(auth, email, password)
          setPopup("Login successful! Redirecting to dashboard...")
          setTimeout(() => navigate("/dashboard"), 1500)
        } else {
          // Non-admin login - check if user exists and handle approval request
          try {
            await signInWithEmailAndPassword(auth, email, password)

            // Check if user has been approved since last check
            const requestStatus = await checkAdminRequestStatus(email)

            if (requestStatus === "approved") {
              // User has been approved, refresh approved admins and allow access
              await fetchApprovedAdmins()
              setPopup("Access approved! Redirecting to dashboard...")
              setTimeout(() => navigate("/dashboard"), 1500)
            } else if (requestStatus === "rejected") {
              setError("Your admin access request has been rejected. Please contact an administrator.")
            } else {
              // User exists but not an admin - create or check approval request
              const status = await createAdminRequest(email)
              if (status === "approved") {
                setPopup("Access approved! Redirecting to dashboard...")
                setTimeout(() => navigate("/dashboard"), 1500)
              } else {
                setPendingApproval(true)
                setPopup("Access request submitted! Existing admins will be notified for approval.")
              }
            }
          } catch (authError) {
            if (authError.code === "auth/user-not-found") {
              setError("No account found with this email. Please sign up first.")
            } else {
              // Other auth errors (wrong password, etc.)
              let errorMessage = "Authentication failed. Please try again."

              switch (authError.code) {
                case "auth/wrong-password":
                  errorMessage = "Incorrect password. Please try again."
                  break
                case "auth/invalid-email":
                  errorMessage = "Please enter a valid email address."
                  break
                case "auth/too-many-requests":
                  errorMessage = "Too many failed attempts. Please try again later."
                  break
                default:
                  errorMessage = authError.message
              }

              setError(errorMessage)
            }
          }
        }
      }
    } catch (err) {
      let errorMessage = "An unexpected error occurred: " + err.message

      // Handle specific Firebase errors
      if (err.code === "auth/email-already-in-use") {
        errorMessage = "An account with this email already exists. Please sign in instead."
      } else if (err.code === "auth/weak-password") {
        errorMessage = "Password is too weak. Please choose a stronger password."
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address."
      }

      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const toggleMode = () => {
    setIsSignUp(!isSignUp)
    setError("")
    setPopup("")
    setPendingApproval(false)
    setEmail("")
    setPassword("")
    setConfirmPassword("")
    setFullName("")
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full mb-4">
            <FaUserShield className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Admin Portal</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {isSignUp ? "Create an account to request admin access" : "Sign in to access the admin dashboard"}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
          {/* Mode Toggle */}
          <div className="flex mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              type="button"
              onClick={() => !isSignUp && toggleMode()}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                !isSignUp
                  ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => isSignUp && toggleMode()}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                isSignUp
                  ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Alert Messages */}
          {popup && (
            <div
              className={`mb-6 p-4 border rounded-lg flex items-center space-x-3 ${
                pendingApproval
                  ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800"
                  : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
              }`}
            >
              {pendingApproval ? (
                <FaClock className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              ) : (
                <FaCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
              )}
              <p
                className={`text-sm ${
                  pendingApproval ? "text-yellow-800 dark:text-yellow-200" : "text-green-800 dark:text-green-200"
                }`}
              >
                {popup}
              </p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center space-x-3">
              <FaExclamationTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name Field - Only for Sign Up */}
            {isSignUp && (
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="fullName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required={isSignUp}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                    placeholder="Enter your full name"
                  />
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                  placeholder={isSignUp ? "Create a password (min 6 characters)" : "Enter your password"}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field - Only for Sign Up */}
            {isSignUp && (
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required={isSignUp}
                    className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors duration-200"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                    ) : (
                      <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin h-5 w-5" />
                  <span>{isSignUp ? "Creating Account..." : "Signing In..."}</span>
                </>
              ) : (
                <span>{isSignUp ? "Create Account" : "Sign In"}</span>
              )}
            </button>
          </form>

          {/* Toggle Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
              <button
                onClick={toggleMode}
                className="font-medium text-green-600 hover:text-green-500 dark:text-green-400 dark:hover:text-green-300 transition-colors duration-200"
              >
                {isSignUp ? "Sign In" : "Sign Up"}
              </button>
            </p>
          </div>

          {/* Info Message */}
          {pendingApproval && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start space-x-3">
                <FaClock className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">Pending Admin Approval</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Your request has been sent to existing administrators. You'll receive access once approved. Try
                    signing in again after approval.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              Need help?{" "}
              <button
                onClick={() => {
                  navigator.clipboard.writeText("iiche@cb.amrita.edu")
                  alert("Email copied to clipboard!")
                }}
                className="font-medium text-green-600 hover:text-green-500 dark:text-green-400 dark:hover:text-green-300 transition-colors duration-200 underline"
              >
                Copy Admin Email
              </button>
            </p>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This is a secure admin portal. Access requires administrator approval.
          </p>
        </div>
      </div>
    </div>
  )
}

export default Signin
